import { useEffect, useMemo, useState } from "react";

import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import {
  INHERIT_BUDGET_MIX_KPI,
  useMMMBudgetMixKpiId,
} from "@features/mmm/hooks/mmm-url-state";
import { efficiencyKpiManager } from "@features/mmm/utils/efficiency-kpi-manager";
import {
  type BudgetMixContributions,
  formatting,
  type FullBudgetMixResponse,
} from "@north-beam/nb-common";
import { useBudgetMixes } from "@pages/mmm/hooks/use-budget-mixes";
import { useClientKpis } from "@pages/mmm/hooks/use-client-kpis";

import { KpiType, type Kpi } from "../client-setting/types";

import { BudgetMixSelect } from "./budget-mix-select";
import { ForecastPacingChart } from "./forecast-pacing-chart";
import { OptimizeKpiSelect } from "./optimize-kpi-select";
import { PredictedValueRangeCard } from "./predicted-value-range-card";
import { SpendCard } from "./spend-card";

type Props = {
  budgetMix: FullBudgetMixResponse;
  inheritedClientSettingKpi: Kpi | null | undefined;
};

export const ScenarioModelingContent = ({
  budgetMix,
  inheritedClientSettingKpi,
}: Props) => {
  const { user } = useUser();
  const { kpis } = useClientKpis();

  const [selectedKpiName, setSelectedKpiName] = useState(kpis[0].kpi_name);

  const { selectedBudgetMixKpiId } = useMMMBudgetMixKpiId();
  useEffect(() => {
    if (
      inheritedClientSettingKpi &&
      selectedBudgetMixKpiId &&
      String(selectedBudgetMixKpiId) === INHERIT_BUDGET_MIX_KPI
    ) {
      setSelectedKpiName(inheritedClientSettingKpi.kpi_name);
    }
  }, [selectedBudgetMixKpiId, inheritedClientSettingKpi, kpis]);

  const [selectedBudgetMixId, setSelectedBudgetMixId] = useState<
    number | null
  >();
  const [budgetMixes] = useBudgetMixes();
  useEffect(() => {
    setSelectedBudgetMixId(budgetMix.id);
  }, [budgetMix]);

  const selectedBudgetMix = useMemo<FullBudgetMixResponse>(
    () =>
      (budgetMixes.find((bm) => bm.id === selectedBudgetMixId) ??
        budgetMix) as FullBudgetMixResponse,
    [budgetMixes, selectedBudgetMixId, budgetMix],
  );

  const summaryData = budgetMix.data.aggregated_data.summary_data;

  const contributions: BudgetMixContributions | null = useMemo(() => {
    return (
      budgetMix.create_budget_recommendations_metadata?.contribution_data?.[
        selectedKpiName
      ] ?? null
    );
  }, [
    budgetMix.create_budget_recommendations_metadata?.contribution_data,
    selectedKpiName,
  ]);

  const selectedKpi = useMemo<Kpi>(
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    () => kpis.find((e) => e.kpi_name === selectedKpiName)!,
    [kpis, selectedKpiName],
  );
  const formatCurrencyApproximate = (value: number) =>
    formatting.formatCurrency(value, {
      precision: "approximate",
      code: user.currencies.currentCurrency,
    });

  const formatNonCurrencyApproximate = (value: number) =>
    formatting.formatMetric(value, "decimal", {
      precision: "approximate",
    });

  const formatRoasCurrencyApproximate = (value: number) =>
    formatting.formatMetric(value, "multiplier", {
      precision: "approximate",
    });

  const isValueTypeRevenue = selectedKpi.kpi_type === KpiType.REVENUE;
  const kpiTypeDisplayName = isValueTypeRevenue ? "Revenue" : "Transaction";

  const formatValueApproximate = (value: number) =>
    isValueTypeRevenue
      ? formatCurrencyApproximate(value)
      : formatNonCurrencyApproximate(value);

  const formatPerformanceMetricApproximate = (value: number) =>
    isValueTypeRevenue
      ? formatRoasCurrencyApproximate(value)
      : formatCurrencyApproximate(value);

  const nameKey = selectedKpi?.kpi_name;
  const predictedValues = nameKey
    ? summaryData.kpi_data[nameKey]?.values
    : null;

  const { efficiencyKpiCalculator, efficiencyKpiType } = efficiencyKpiManager(
    selectedKpi,
    user,
  );
  let derivedMetricLow = 0;
  let derivedMetricHigh = 0;
  let derivedMetricMedian = 0;
  let derivedIncrementalMetric = 0;

  if (predictedValues) {
    derivedMetricLow = efficiencyKpiCalculator(
      summaryData.spend,
      predictedValues.low,
    );
    derivedMetricHigh = efficiencyKpiCalculator(
      summaryData.spend,
      predictedValues.high,
    );
    derivedMetricMedian = efficiencyKpiCalculator(
      summaryData.spend,
      predictedValues.median,
    );
    derivedIncrementalMetric = efficiencyKpiCalculator(
      summaryData.spend,
      (contributions?.contribution_spend ?? 0) +
        (contributions?.contribution_spend_times_promo ?? 0),
    );
  }

  return (
    <>
      <div className="m-6 space-y-4">
        <h2 className="text-2xl my-0 font-semibold">Forecast pacing</h2>
        <div className="flex items-center justify-between">
          <BudgetMixSelect
            selectedBudgetMix={selectedBudgetMix}
            setSelectedBudgetMixId={setSelectedBudgetMixId}
            selectedKpi={selectedKpi}
            formatCurrencyApproximate={formatCurrencyApproximate}
            kpiTypeDisplayName={kpiTypeDisplayName}
            formatValueApproximate={formatValueApproximate}
          />
          <OptimizeKpiSelect
            selectedKpiName={selectedKpiName}
            setSelectedKpiName={setSelectedKpiName}
            inheritedClientSettingKpi={inheritedClientSettingKpi}
          />
        </div>
        <div className="flex items-center gap-2.5">
          <PredictedValueRangeCard
            predictedValues={predictedValues}
            kpiTypeDisplayName={kpiTypeDisplayName}
            formatValueApproximate={formatValueApproximate}
          />
          {predictedValues && (
            <PredictedValueRangeCard
              predictedValues={{
                low: derivedMetricLow,
                median: derivedMetricMedian,
                high: derivedMetricHigh,
              }}
              kpiTypeDisplayName={`${efficiencyKpiType}`}
              formatValueApproximate={formatPerformanceMetricApproximate}
            >
              {derivedIncrementalMetric && (
                <div className="flex flex-row items-center">
                  <span className="text-sm font-semibold ">
                    <GenericTooltip
                      content={
                        <IncrementalEfficiencyExplainer
                          efficiencyKpiType={efficiencyKpiType}
                        />
                      }
                      noInfoCircle
                    >
                      <div>i{efficiencyKpiType}</div>
                    </GenericTooltip>
                    <div className="text-cosmic">
                      {formatPerformanceMetricApproximate(
                        derivedIncrementalMetric,
                      )}
                    </div>
                  </span>
                </div>
              )}
            </PredictedValueRangeCard>
          )}
          <SpendCard
            summaryData={summaryData}
            formatCurrencyApproximate={formatCurrencyApproximate}
          />
        </div>
        <ForecastPacingChart
          selectedKpi={selectedKpi}
          selectedBudgetMix={selectedBudgetMix}
        />
      </div>
    </>
  );
};

export function IncrementalEfficiencyExplainer({
  efficiencyKpiType,
}: {
  efficiencyKpiType: "ROAS" | "CAC";
}) {
  return efficiencyKpiType === "ROAS" ? (
    <div>
      iROAS is your ROAS against incremental revenue driven by media spend.
      <div>This omits revenue coming from non-media and organic sources</div>
    </div>
  ) : (
    <div>
      iCAC is your CAC using only incremental conversions driven by media spend.
      <div>This omits revenue coming from non-media and organic sources</div>
    </div>
  );
}
